import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Typography, Box, LinearProgress } from '@mui/material'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { useSessionTimeout } from '../../utils/hooks/useSessionTimeout'
import CustomAlertDialog from './CustomAlertDialog/CustomAlertDialog'

interface SessionTimeoutWrapperProps {
	children: React.ReactNode
	/**
	 * Timeout duration in minutes
	 * Default: 20 minutes
	 */
	timeoutMinutes?: number
	/**
	 * Warning time before logout in minutes
	 * Default: 2 minutes
	 */
	warningMinutes?: number
	/**
	 * Whether to enable session timeout
	 * Default: true
	 */
	enabled?: boolean
}

export const SessionTimeoutWrapper: React.FC<SessionTimeoutWrapperProps> = ({
	children,
	timeoutMinutes = 20,
	warningMinutes = 2,
	enabled = true,
}) => {
	const [showWarning, setShowWarning] = useState(false)
	const [countdown, setCountdown] = useState(0)
	const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null)

	const clearCountdownInterval = useCallback(() => {
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current)
			countdownIntervalRef.current = null
		}
	}, [])

	const handleWarning = useCallback(() => {
		setShowWarning(true)

		// Clear any existing interval
		clearCountdownInterval()

		// Start countdown timer
		const warningDuration = warningMinutes * 60 // Convert to seconds
		setCountdown(warningDuration)

		countdownIntervalRef.current = setInterval(() => {
			setCountdown(prev => {
				if (prev <= 1) {
					clearInterval(countdownIntervalRef.current!)
					countdownIntervalRef.current = null
					return 0
				}
				return prev - 1
			})
		}, 1000)
	}, [warningMinutes, clearCountdownInterval])

	const handleTimeout = useCallback(() => {
		// This will be handled by the useSessionTimeout hook
	}, [])

	const { refreshSession, getRemainingTime } = useSessionTimeout({
		timeoutDuration: timeoutMinutes * 60 * 1000, // Convert minutes to milliseconds
		warningTime: warningMinutes * 60 * 1000, // Convert minutes to milliseconds
		onWarning: handleWarning,
		onTimeout: handleTimeout,
		enabled,
	})

	const handleContinueSession = useCallback(() => {
		setShowWarning(false)
		setCountdown(0)
		clearCountdownInterval()
		refreshSession()
	}, [refreshSession, clearCountdownInterval])

	const progress = countdown > 0 ? Math.round(((warningMinutes * 60 - countdown) / (warningMinutes * 60)) * 100) : 0
	const clampedProgress = Math.min(100, Math.max(0, progress))
	const countdownSeconds = Math.max(0, Math.floor(countdown))
	const countdownLabel = `${countdownSeconds} ${countdownSeconds === 1 ? 'segundo restante' : 'segundos restantes'}`

	// Cleanup interval on unmount
	useEffect(() => {
		return () => {
			clearCountdownInterval()
		}
	}, [clearCountdownInterval])

	if (!enabled) {
		return <>{children}</>
	}

	return (
		<>
			{children}

			<CustomAlertDialog
				open={showWarning}
				severity='warning'
				icon={<WarningRoundedIcon sx={{ fontSize: 35 }} />}
				title={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '32px',
							alignItems: 'stretch',
							width: '100%',
							pb: '16px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								alignItems: 'center',
								width: '100%',
								textAlign: 'center',
								color: 'var(--primary-dark)',
							}}
						>
							<Typography
								component='p'
								sx={{
									m: 0,
									width: '100%',
									fontFamily: 'var(--font-family)',
									fontSize: '16px',
									fontWeight: 600,
									lineHeight: 1.75,
									letterSpacing: '0.15px',
									color: 'var(--primary-dark)',
								}}
							>
								A sua Sessão irá expirar devido a inatividade
							</Typography>

							<Typography
								component='p'
								sx={{
									m: 0,
									width: '100%',
									fontFamily: 'var(--font-family)',
									fontSize: '13px',
									fontWeight: 500,
									lineHeight: 1.3,
									letterSpacing: 0,
									color: 'var(--primary-dark)',
								}}
							>
								Clique em “Continuar Sessão” para permanecer ligado ou aguarde o redirecionamento automático para o
								Login.
							</Typography>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								flexWrap: 'wrap',
								gap: '32px',
								width: '100%',
							}}
						>
							<LinearProgress
								variant='determinate'
								value={clampedProgress}
								sx={{
									flex: '1 1 200px',
									minWidth: 'min(200px, 100%)',
									height: 4,
									borderRadius: 0,
									backgroundColor: 'rgba(0, 165, 255, 0.4)',
									'& .MuiLinearProgress-bar': {
										backgroundColor: '#00A5FF',
									},
								}}
							/>

							<Typography
								component='p'
								sx={{
									m: 0,
									fontFamily: 'var(--font-family)',
									fontSize: '14px',
									fontWeight: 600,
									lineHeight: 1.57,
									letterSpacing: 0,
									color: 'var(--primary-dark)',
									whiteSpace: 'nowrap',
								}}
							>
								{countdownLabel}
							</Typography>
						</Box>
					</Box>
				}
				closeOnEscapeKeyDown={false}
				closeOnBackdropClick={false}
				showCloseButton={false}
				maxWidth='xs'
				PaperProps={{
					sx: {
						width: '444px',
						maxWidth: 'calc(100vw - 32px)',
						borderRadius: '4px',
						backgroundColor: '#FFFFFF',
						backgroundImage: 'linear-gradient(180deg, rgba(255, 92, 0, 0.10) 0%, rgba(255, 92, 0, 0) 100%)',
						boxShadow:
							'0px 6px 30px 5px rgba(9, 32, 86, 0.12), 0px 16px 24px 2px rgba(9, 32, 86, 0.14), 0px 8px 10px -5px rgba(9, 32, 86, 0.20)',
					},
				}}
				iconContainerSx={{
					background: 'linear-gradient(92.9205deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
					boxShadow: 'none',
				}}
				dialogActionsProps={{
					sx: {
						padding: '8px',
					},
				}}
				primaryAction={{
					label: 'Continuar Sessão',
					onClick: handleContinueSession,
					variant: 'contained',
					color: 'primary',
					autoFocus: true,
					sx: {
						minHeight: '36px',
						minWidth: 'auto',
						padding: '6px 16px',
						borderRadius: '4px',
						background: 'linear-gradient(90.0556deg, var(--primary-dark) 0%, #00A5FF 99.972%)',
						boxShadow: 'none',
						fontFamily: 'var(--font-family)',
						fontSize: '14px',
						fontWeight: 600,
						lineHeight: '24px',
						letterSpacing: 0,
						'&:hover': {
							background: 'linear-gradient(90.0556deg, var(--primary-dark) 0%, #00A5FF 99.972%)',
							boxShadow: 'none',
						},
					},
				}}
			/>
		</>
	)
}
